import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";

import { OVERLAY_PREF } from "../constants";

interface ILogbookRefsContext {
  getInputRefProps: (
    id: string,
    numberOfCols?: number,
    currentRowIndex?: number,
    totalRows?: number,
  ) => {
    ref: (ref: any) => void;
    onKeyDown: (event: any) => void;
  };
}

const LogbookRefsContext = createContext<ILogbookRefsContext | null>(null);

export const LogbookRefsProvider = ({ children }: { children: ReactNode }) => {
  const inputRefs = useRef<Record<string, any>>({});

  useEffect(() => {
    inputRefs.current = {};
  }, []);

  const addInputRef = (id: string, ref: any) => {
    if (!id.includes(OVERLAY_PREF)) {
      if (ref) {
        inputRefs.current[id] = ref;
      } else {
        // Clean up null refs so stale entries don't accumulate
        delete inputRefs.current[id];
      }
    }
  };

  /**
   * Returns ref IDs sorted by their actual DOM position (top-to-bottom, left-to-right).
   * This ensures correct navigation order
   */
  const getOrderedIds = () => {
    const entries = Object.entries(inputRefs.current).filter(
      ([, ref]) => ref && typeof ref.getBoundingClientRect === "function",
    );

    entries.sort(([, a], [, b]) => {
      const rectA = a.getBoundingClientRect();
      const rectB = b.getBoundingClientRect();
      // Sort by vertical position first, then horizontal
      if (Math.abs(rectA.top - rectB.top) > 2) return rectA.top - rectB.top;
      return rectA.left - rectB.left;
    });

    return entries.map(([id]) => id);
  };

  const handleKeyDown = (
    event: any,
    id: string,
    numberOfCols?: number,
    currentRowIndex?: number,
    totalRows?: number,
  ) => {
    if (event.key === "Tab") {
      event.preventDefault();
      const ids = getOrderedIds();
      const currentIndex = ids.indexOf(id);

      if (currentIndex !== -1) {
        const nextElement = event.shiftKey
          ? inputRefs.current[ids[currentIndex - 1]]
          : inputRefs.current[ids[currentIndex + 1]];
        if (nextElement && "focus" in nextElement) {
          nextElement.focus();
        }
      }
    }
    if (event.key === "Enter" && numberOfCols) {
      event.preventDefault();
      const ids = getOrderedIds();
      const currentIndex = ids.indexOf(id);

      if (currentIndex !== -1) {
        // Handle edge cases for first and last row
        if (event.shiftKey && currentRowIndex === 0) {
          // const bottomRowIndex =
          //   currentIndex + ((totalRows ?? 1) - 1) * numberOfCols;
          // const bottomElement = inputRefs.current[ids[bottomRowIndex]];
          // if (bottomElement && "focus" in bottomElement) {
          //   bottomElement.focus();
          // }
          return;
        }
        if (!event.shiftKey && currentRowIndex === (totalRows ?? 0) - 1) {
          // const topRowIndex = currentIndex - currentRowIndex * numberOfCols;
          // const topElement = inputRefs.current[ids[topRowIndex]];
          // if (topElement && "focus" in topElement) {
          //   topElement.focus();
          // }
          return;
        }

        // Calculate next/previous element index based on number of columns (vertical jump)
        const nextIndex = event.shiftKey
          ? currentIndex - numberOfCols
          : currentIndex + numberOfCols;

        const nextElement = inputRefs.current[ids[nextIndex]];
        if (nextElement && "focus" in nextElement) {
          nextElement.focus();
        }
      }
    }
  };

  const getInputRefProps = useCallback(
    (
      id: string,
      numberOfCols?: number,
      currentRowIndex?: number,
      totalRows?: number,
    ) => ({
      ref: (ref: any) => addInputRef(id, ref),
      onKeyDown: (e: any) =>
        handleKeyDown(e, id, numberOfCols, currentRowIndex, totalRows),
    }),
    [],
  );

  return (
    <LogbookRefsContext.Provider value={{ getInputRefProps }}>
      {children}
    </LogbookRefsContext.Provider>
  );
};

export const useLogbookRefsContext = (
  id: string,
  numberOfCols?: number,
  currentRowIndex?: number,
  totalRows?: number,
) => {
  const context = useContext(LogbookRefsContext);
  if (!context) {
    throw new Error(
      "useLogbookRefsContext must be used within a LogbookRefsProvider",
    );
  }
  return context.getInputRefProps(id, numberOfCols, currentRowIndex, totalRows);
};
